<template>
  <div>
    <div class="columns row-table-headings">
      <div class="column column-no">{{ index + 1 }}.</div>
      <div class="column column-criteria-title">
        <CriteriaViewUpdateComponent
          :class="$style.criteriaTitleContainer"
          :criteria="criteriaOption.criteria"
        />
        <b-collapse
          v-if="criteriaOption.criteria.guide !== ''"
          :open="false"
          aria-id="contentIdForA11y1"
        >
          <template #trigger="props">
            <div class="guide-btn-container">
              <b-button
                :label="!props.open ? 'Show Guide' : 'Hide Guide'"
                class="guide-trigger-btn"
                aria-controls="contentIdForA11y1"
              />
              <b-icon
                :icon="!props.open ? 'chevron-down' : 'chevron-up'"
                size="is-medium"
              >
              </b-icon>
            </div>
          </template>
          <div :class="$style.guideSection">
            <div class="content">
              {{ criteriaOption.criteria.guide }}
            </div>
          </div>
        </b-collapse>
      </div>
    </div>
    <div class="columns row-table-content">
      <div class="column column-empty"></div>
      <div class="column column-table-data">
        <div :class="[$style.rangeValueContainer]">
          <table class="table is-fullwidth">
            <thead>
              <tr>
                <th :class="$style.answerChoicesHeaderStyle">Answer Choices</th>
                <th :class="$style.answerHeaderStyle">Answer</th>
                <th :class="$style.totalHeaderStyle">
                  Total To-Date
                  <b-tooltip
                    v-if="totalToDateMessage"
                    label="Includes the value currently being edited"
                    position="is-bottom"
                    :class="$style.infoTooltip"
                  >
                    <b-icon
                      icon="information-outline"
                      size="is-small"
                      custom-size="mdi-15px"
                      :custom-class="$style.infoIcon"
                    >
                    </b-icon>
                  </b-tooltip>
                </th>
              </tr>
            </thead>

            <tbody>
              <tr>
                <td :class="$style.answerChoicesBodyStyle">
                  <section>
                    <b-field>
                      <b-input
                        v-if="hasSelectedNA"
                        type="number"
                        :controls="false"
                        :class="$style.disableNumberInput"
                        disabled
                      ></b-input>
                      <b-input
                        v-else
                        v-model="sliderValue"
                        type="number"
                        :controls="false"
                        :class="$style.numberInput"
                        onkeydown="if(event.key==='.'){event.preventDefault();}"
                      ></b-input>
                      <span :class="$style.rangeValue"
                        ><span style="font-weight: bold">Min:</span>
                        {{ minValue }},
                        <span style="font-weight: bold">Max:</span>
                        {{ maxValue }}</span
                      >
                    </b-field>
                    <p
                      v-if="showRangeErrorMessage"
                      :class="$style.validationErrorMessage"
                    >
                      Value must be between {{ minValue }} to {{ maxValue }}
                    </p>
                    <div style="display: flex">
                      <a v-if="hasSelectedNA" @click="handleMarkedNAValue()">
                        <b-icon
                          icon="radiobox-marked"
                          size="is-small"
                          :custom-class="$style.iconsColorMarked"
                          custom-size="mdi-24px"
                        >
                        </b-icon>
                      </a>
                      <a v-else @click="handleUnmarkedNAValue()">
                        <b-icon
                          icon="radiobox-blank"
                          size="is-small"
                          :custom-class="$style.iconsColorUnmarked"
                          custom-size="mdi-24px"
                        >
                        </b-icon>
                      </a>
                      <div style="padding-left: 10px">Submit answer as N/A</div>
                    </div>

                    <br />
                    <br />
                    <br />
                    <b-field style="padding: 0px 13px">
                      <Slider
                        v-if="sliderValue === ''"
                        v-model="sliderValue"
                        :max="maxValue"
                        :min="minValue"
                        :options="{
                          start: [minValue],
                        }"
                      />
                      <Slider
                        v-else
                        v-model="sliderValue"
                        :max="maxValue"
                        :min="minValue"
                        :tooltips="disableTooltipSlider"
                      />
                    </b-field>
                  </section>
                </td>
                <td :class="$style.answerBodyStyle">
                  <span v-if="hasSelectedNA">N/A</span>
                  <span v-else>{{
                    sliderValue >= minValue ? sliderValue : ''
                  }}</span>
                </td>
                <td :class="$style.totalBodyStyle">
                  {{ totalToDateValue }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    <div
      class="columns row-table-comment"
      v-if="
        !criteriaOption.isCommentSectionExpanded &&
        resultData.criteriaData.length > 1
      "
    >
      <div class="column column-empty-comment"></div>
      <div
        class="column column-data-comment"
        @click="showCommentBox(criteriaOption)"
      >
        + Add Comment
      </div>
    </div>
    <div
      class="columns row-table-comment"
      v-if="
        criteriaOption.isCommentSectionExpanded ||
        resultData.criteriaData.length === 1
      "
    >
      <div class="column column-empty-comment"></div>
      <div class="column column-data-comment-box">
        <b-field label="Comment">
          <b-input v-model="criteriaOption.comments" type="textarea"> </b-input>
        </b-field>
      </div>
    </div>
    <div></div>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import {
  DataCollectionSubmitRequestPayload,
  DataCollectionSummaryCriteria,
  RangeOptionValueType,
  DataCollectionCriteriaDataPayload,
} from '@/store/modules/audits/types/audits.types';
import Slider from '@vueform/slider/dist/slider.vue2.js';
import { isDifferent } from '@/jbi-shared/util/watcher.vue-decorator';
import CriteriaViewUpdateComponent from '../../Project/components/CriteriaViewUpdateComponent.vue';

@Component({
  components: {
    Slider,
    CriteriaViewUpdateComponent,
  },
})
export default class DataCollectionRangeCriteriaIndividual extends Vue {
  @Prop()
  public index!: number;

  @Prop()
  public criteriaOption!: DataCollectionCriteriaDataPayload;

  @Prop()
  public totalToDateMessage!: boolean;

  @Prop()
  public resultData!: DataCollectionSubmitRequestPayload;

  @Prop()
  public editDataCollectionRecord!: any;

  @Prop()
  public criteriaSamplingData!: any;

  @Prop()
  public tryToSubmitKey!: number;

  public sliderValue: number | string = '';
  public tempSliderValue: number | string = '';
  public showRangeErrorMessage: boolean = false;
  public showCountErrorMessage: boolean = false;
  public showValueErrorMessage: boolean = false;
  public hasSelectedNA: boolean = false;
  public isMarkedAsNA: boolean = false;
  public answerValueDetail: RangeOptionValueType[] = [];

  public mounted() {
    this.answerValueDetail = this.criteriaOption.optionValues;
    if (this.editDataCollectionRecord) {
      const editedRangeValue = this.answerValueDetail.find(
        (data: RangeOptionValueType) => Number(data.value) === 1,
      );

      if (editedRangeValue) {
        if (editedRangeValue.option === 'N/A') {
          this.hasSelectedNA = true;
          this.sliderValue = this.minValue;
        } else {
          this.sliderValue = editedRangeValue.option;
        }
      }
    }
    this.$emit('valuePopulated');
  }

  get disableTooltipSlider(): boolean {
    return this.hasSelectedNA ? false : true;
  }

  get totalToDateValue(): number {
    let totalToDate = 0;
    if (this.editDataCollectionRecord && this.criteriaSamplingData) {
      const existingTotalToDateValue = this.criteriaSamplingData.find(
        (data: RangeOptionValueType) => {
          if (this.hasSelectedNA) {
            return data.option === 'N/A';
          } else {
            return data.option === this.sliderValue.toString();
          }
        },
      );

      totalToDate = existingTotalToDateValue
        ? existingTotalToDateValue.totalToDate
        : totalToDate;
    } else {
      const existingOptionValue = this.criteriaOption.optionValues.find(
        (data: RangeOptionValueType) => {
          if (this.hasSelectedNA) {
            return data.option === 'N/A';
          } else {
            return data.option === this.sliderValue.toString();
          }
        },
      );

      totalToDate = existingOptionValue
        ? existingOptionValue.totalToDate
        : totalToDate;
    }

    return totalToDate;
  }

  get minValue(): number {
    return parseInt(
      JSON.parse(this.criteriaOption.criteria.criteriaOptions)[0],
      10,
    );
  }

  get maxValue(): number {
    return parseInt(
      JSON.parse(this.criteriaOption.criteria.criteriaOptions)[1],
      10,
    );
  }

  public showCommentBox(rowData: DataCollectionCriteriaDataPayload) {
    rowData.isCommentSectionExpanded = !rowData.isCommentSectionExpanded;
  }

  public handleUnmarkedNAValue() {
    this.hasSelectedNA = true;
    this.tempSliderValue = this.sliderValue;
    this.sliderValue = this.minValue;
    this.showRangeErrorMessage = false;

    const lastAnswerIndex = this.answerValueDetail[
      this.answerValueDetail.length - 1
    ];
    if (lastAnswerIndex.option !== 'N/A' && lastAnswerIndex.totalToDate === 0) {
      this.answerValueDetail.pop();
    }
    this.answerValueDetail.map((item: RangeOptionValueType) => {
      if (item.option === 'N/A') {
        item.value = 1;
      } else {
        item.value = 0;
      }
    });
    this.resultData.criteriaData.forEach(
      (element: DataCollectionCriteriaDataPayload) => {
        if (element.criteria.id === this.criteriaOption.criteria.id) {
          element.optionValues = this.answerValueDetail;
        }
      },
    );
    this.$emit('updateSubmitButton', { index: this.index, isError: false });
  }

  public handleMarkedNAValue() {
    this.hasSelectedNA = false;
    this.sliderValue = this.tempSliderValue
      ? this.tempSliderValue
      : this.minValue;
    const lastAnswerIndex = this.answerValueDetail[
      this.answerValueDetail.length - 1
    ];
    if (lastAnswerIndex.option !== 'N/A' && lastAnswerIndex.totalToDate === 0) {
      this.answerValueDetail.pop();
    }
    this.answerValueDetail.map((item: RangeOptionValueType) => {
      if (Number(item.option) === this.minValue) {
        item.value = 1;
      } else {
        item.value = 0;
      }
    });
    this.resultData.criteriaData.forEach(
      (element: DataCollectionCriteriaDataPayload) => {
        if (element.criteria.id === this.criteriaOption.criteria.id) {
          element.optionValues = this.answerValueDetail;
        }
      },
    );
  }

  @Watch('sliderValue', { deep: true })
  @isDifferent
  public watchSliderValue(newValue: number | string) {
    if (this.hasSelectedNA && newValue === this.minValue) {
      return;
    }

    this.sliderValue = newValue;
    this.hasSelectedNA = false;

    this.showRangeErrorMessage =
      newValue < this.minValue ||
      newValue > this.maxValue ||
      this.sliderValue === ''
        ? true
        : false;

    this.$emit('updateSubmitButton', {
      index: this.index,
      isError: this.showRangeErrorMessage,
    });

    if (!this.showValueErrorMessage) {
      const existingRangeValue = this.answerValueDetail.find(
        (data: RangeOptionValueType) =>
          Number(data.option) === Number(newValue),
      );
      const lastAnswerIndex = this.answerValueDetail[
        this.answerValueDetail.length - 1
      ];
      if (
        lastAnswerIndex.option !== 'N/A' &&
        lastAnswerIndex.totalToDate === 0
      ) {
        this.answerValueDetail.pop();
      }
      this.answerValueDetail.map((item: RangeOptionValueType) => {
        if (Number(newValue) === Number(item.option)) {
          item.value = 1;
        } else {
          item.value = 0;
        }
      });
      if (!existingRangeValue) {
        this.answerValueDetail.push({
          option: newValue,
          totalToDate: 0,
          value: 1,
        });
      }

      this.resultData.criteriaData.forEach(
        (element: DataCollectionCriteriaDataPayload) => {
          if (element.criteria.id === this.criteriaOption.criteria.id) {
            element.optionValues = this.answerValueDetail;
          }
        },
      );
    }
  }

  @Watch('tryToSubmitKey')
  public watchSubmitAction(): void {
    if (this.sliderValue === '') {
      this.showRangeErrorMessage = true;
      this.$emit('updateSubmitButton', {
        index: this.index,
        isError: this.showRangeErrorMessage,
      });
    }
  }
}
</script>
<style src="@vueform/slider/themes/default.css"></style>
<style lang="scss" module>
.bField .label {
  color: #9f9f9f;
  font-size: 14px;
  border-bottom-right-radius: 5px;
}
.numberInput {
  width: 80px !important;
  height: 44px;
  // border-radius: 5px;
  input {
    background-color: white !important;
    border-color: #dfdfdf !important;
    color: #444f51 !important;
    text-align: right;
    height: 44px;
    padding: 7px 7px;
    border-bottom-right-radius: 3px !important;
    border-top-right-radius: 3px !important;
  }
}

.disableNumberInput {
  width: 80px !important;
  height: 44px;
  input {
    border-color: #dfdfdf !important;
    color: #c9c0c0 !important;
    text-align: right;
    height: 44px;
    padding: 7px 7px;
  }
}

input[type='number'] {
  -moz-appearance: textfield;
}
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
}
.criteriaTitleContainer {
  margin-bottom: 8px;
}
.guideSection {
  color: #9f9f9f;
  font-size: 14px;
  margin-top: 8px;
}
.validationErrorMessage {
  color: red;
  font-size: 12px;
  margin-top: 4px;
}
.rangeValue {
  padding-top: 10px;
  padding-left: 20px;
  color: #b4b3b3;
  font-size: 14px;
}
.rangeValueContainer {
  border: 1px solid #dfdfdf;
  border-top-right-radius: 4px;
  border-top-left-radius: 4px;
  border-bottom-right-radius: 4px;
  border-bottom-left-radius: 4px;
  overflow: hidden;
  & > table {
    thead {
      background-color: #ffffff;
      border-bottom: 1px solid #dfdfdf;
      tr {
        font-size: 14px;
        th {
          color: #9f9f9f;
          font-weight: initial !important;
          padding: 13px 12px 13px 18px;
        }
        th:first-child {
          padding-left: 20px;
        }
      }
    }
    tbody {
      background-color: #ffffff;
      tr {
        td {
          color: #444f51;
          border: 0px;
        }
      }
    }
  }
}
.iconsColorUnmarked {
  color: #dfdfdf;
  padding-top: 10px;
}
.iconsColorMarked {
  color: #21b5ea;
  padding-top: 10px;
}
.answerChoicesHeaderStyle {
  width: 90% !important;
  border-right: 1px solid #dfdfdf !important;
}
.answerHeaderStyle {
  width: 10% !important;
  text-align: center !important;
  border-right: 1px solid #dfdfdf !important;
}
.totalHeaderStyle {
  width: 10% !important;
  text-align: center !important;
}
.answerChoicesBodyStyle {
  border-right: 1px solid #dfdfdf !important;
  padding-right: 10px !important;
  height: 100px !important;
}
.answerBodyStyle {
  padding: 30px 20px 20px 300px !important;
  border-right: 1px solid #dfdfdf !important;
  height: 100px !important;
  color: #b4b3b3 !important;
}
.totalBodyStyle {
  padding: 30px 20px 20px 300px !important;
  height: 100px !important;
  color: #b4b3b3 !important;
}
.infoTooltip {
  position: absolute !important;
  display: inline-block !important;
}
.infoIcon {
  color: #21b5ea;
  margin-left: 8px !important;
}
</style>
